import { Fragment, useCallback, useRef, forwardRef } from 'react';
import { observer } from 'decorators';
import { useDisposable } from 'hooks';
import { Grid, FormControl } from '@material-ui/core';
import Select, { SelectAsync } from 'components/select';
import Modal from 'components/modal';
import Button from 'components/button';

import TimezonePickerStore from './timezonePicker.store';

export default observer(forwardRef(function TimezonePicker({ value, isLoading: externalLoading, onChange: handleChange, notEmpty, detectDefault, countryCode, culture, ...other }, ref) {
  const lastRef = useRef(value);
  const store = useDisposable(() => new TimezonePickerStore({ value, notEmpty, detectDefault, countryCode, culture, onChange: handleChange }), [ notEmpty, detectDefault, countryCode, culture ]);

  if (lastRef.current !== value) {
    store.setNewValue(value);
    lastRef.current = value;
  }

  const handleChangeWrap = useCallback(e => store.onChange(e, handleChange), [ store, handleChange ]);

  const { isLoading, options } = store;
  const allOpts = Object.assign({ }, other, {
    value,
    options,
    valueKey: 'id',
    nameKey: 'name',
    onChange: handleChangeWrap
  });

  return <Fragment>
    <Select ref={ref} isLoading={isLoading || externalLoading} {...allOpts} />
    <TimezoneFullPicker store={store} onChange={handleChange} />
  </Fragment>;
}));

const TimezoneFullPicker = observer(function TimezoneFullPicker({ store, onChange }) {
  const { showFullPicker, country, loadingTimezones, countryTimezones, pickedTimezone } = store;

  const handleClose = useCallback(() => store.closePicker(), [ store ]);
  const handleCountry = useCallback(e => store.onChangeCountry(e), [ store ]);
  const handleSelect = useCallback(e => store.selectTimezone(e), [ store ]);
  const handleSet = useCallback(() => store.setTimezone(onChange), [ store, onChange ]);

  const search = useCallback(q => store.searchCountries(q), [ store ]);

  if (!showFullPicker) { return null; }

  return <Modal label="Select Time Zone" key="modal" open onClose={handleClose}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <SelectAsync
            valueKey="code"
            value={country}
            placeholder="Search Countries"
            onChange={handleCountry}
            loadOptions={search}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <Select
            disabled={!country}
            isLoading={loadingTimezones}
            valueKey="id"
            options={countryTimezones}
            value={pickedTimezone}
            onChange={handleSelect}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs />
          <Grid item>
            <Button onClick={handleClose}>Cancel</Button>
            <Button color="primary" disabled={!pickedTimezone} onClick={handleSet}>Update</Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </Modal>;
});